import { clsx } from "clsx";
import {
  AlertTriangle,
  CalendarRange,
  Lock,
  Medal,
  Pencil,
  Play,
  Plus,
  Trash2,
  Trophy,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PERMISSIONS } from "@/auth/permissions";
import {
  Alert,
  Badge,
  Button,
  Card,
  Drawer,
  EmptyState,
  Modal,
  Spinner,
} from "@/components/common";
import {
  ContestModal,
  type ContestTarget,
} from "@/components/contests/ContestModal";
import {
  formatPeriod,
  formatValue,
  MEASURE_HINTS,
  MEASURE_LABELS,
  REWARD_TYPE_LABELS,
  rewardSummary,
  STATUS_LABELS,
  STATUS_VARIANTS,
  scopeSummary,
} from "@/components/contests/format";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  useActivateContest,
  useContest,
  useContests,
  useDeleteContest,
  useFinishContest,
} from "@/hooks/useContests";
import { useHasPermission } from "@/hooks/usePermission";
import type {
  Contest,
  ContestListItem,
  ContestRankingEntry,
  ContestStanding,
} from "@/types/contests";

function errorMessage(error: unknown, fallback: string) {
  return (error as { message?: string } | null)?.message ?? fallback;
}

function ProgressBar({ value }: { value: number }) {
  return (
    <div
      className="h-2 overflow-hidden rounded-full bg-muted"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
    >
      <div
        className="h-full rounded-full bg-adara-600 transition-all"
        style={{ width: `${Math.round(value * 100)}%` }}
      />
    </div>
  );
}

/** The current agent's place and progress to the next goal. */
function Standing({ contest, me }: { contest: Contest; me: ContestStanding }) {
  return (
    <div className="space-y-2 rounded-lg bg-adara-50 p-3 dark:bg-adara-900/20">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm text-sub">
          Twoje miejsce:{" "}
          <span className="text-lg font-semibold text-main">{me.place}</span> z{" "}
          {me.participantCount}
        </p>
        <p className="text-sm font-semibold text-main">
          {formatValue(contest.measure, me.value)}
        </p>
      </div>
      {me.target !== null && me.progress !== null && (
        <div className="space-y-1">
          <ProgressBar value={me.progress} />
          <p className="flex justify-between gap-2 text-xs text-dim">
            <span>{me.targetLabel}</span>
            <span>
              {me.value >= me.target
                ? "osiągnięty"
                : `brakuje ${formatValue(contest.measure, me.target - me.value)}`}
            </span>
          </p>
        </div>
      )}
      {me.reward && (
        <p className="flex items-center gap-1.5 text-xs font-medium text-green-700 dark:text-green-400">
          <Medal size={14} aria-hidden />
          {contest.status === "Finished" ? "Nagroda" : "Na dziś"}: {me.reward}
        </p>
      )}
    </div>
  );
}

function ContestCard({
  item,
  onOpen,
}: {
  item: ContestListItem;
  onOpen: () => void;
}) {
  const { contest, me } = item;
  return (
    <Card padding="none">
      <button
        type="button"
        onClick={onOpen}
        className="block w-full space-y-3 rounded-xl p-4 text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-adara-500"
      >
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-base font-semibold text-main">{contest.name}</h3>
          <Badge variant={STATUS_VARIANTS[contest.status]} dot>
            {STATUS_LABELS[contest.status]}
          </Badge>
        </div>
        <div className="space-y-1 text-sm text-dim">
          <p className="flex items-center gap-1.5">
            <CalendarRange size={14} aria-hidden />
            {formatPeriod(contest)}
          </p>
          <p>
            <span className="font-medium text-sub">
              {MEASURE_LABELS[contest.measure]}
            </span>{" "}
            · {rewardSummary(contest)}
          </p>
          <p className="truncate text-xs">{scopeSummary(contest)}</p>
        </div>
        {me ? (
          <Standing contest={contest} me={me} />
        ) : (
          contest.status !== "Draft" && (
            <p className="text-xs text-faint">
              Uczestników: {item.participantCount}
            </p>
          )
        )}
      </button>
    </Card>
  );
}

function RankingTable({
  contest,
  ranking,
}: {
  contest: Contest;
  ranking: ContestRankingEntry[];
}) {
  if (ranking.length === 0) {
    return <p className="text-sm text-faint">Brak uczestników.</p>;
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-edge-soft text-left text-xs text-faint">
          <th className="py-2 pr-2 font-medium">#</th>
          <th className="py-2 pr-2 font-medium">Agent</th>
          <th className="py-2 pr-2 text-right font-medium">Wynik</th>
          <th className="py-2 font-medium">Nagroda</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-edge-soft">
        {ranking.map((r) => (
          <tr
            key={r.agentId}
            className={clsx(r.qualified ? "text-main" : "text-dim")}
          >
            <td className="py-2 pr-2 font-semibold">{r.place}</td>
            <td className="py-2 pr-2">{r.agentName}</td>
            <td className="py-2 pr-2 text-right tabular-nums">
              {formatValue(contest.measure, r.value)}
            </td>
            <td className="py-2">
              {r.reward ? (
                <Badge variant="success">{r.reward}</Badge>
              ) : (
                <span className="text-faint">—</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ContestDrawer({
  contestId,
  onClose,
  onEdit,
  onFinish,
  onDelete,
}: {
  contestId: string | null;
  onClose: () => void;
  onEdit: (contest: Contest) => void;
  onFinish: (contest: Contest) => void;
  onDelete: (contest: Contest) => void;
}) {
  const { data, isLoading } = useContest(contestId);
  const activate = useActivateContest();
  const canUpdate = useHasPermission(PERMISSIONS.CONTESTS.UPDATE);
  const canDelete = useHasPermission(PERMISSIONS.CONTESTS.DELETE);
  const contest = data?.contest;

  const footer = contest && data?.canManage && (
    <div className="flex flex-wrap justify-end gap-2">
      {contest.status === "Draft" && canDelete && (
        <Button
          variant="ghost"
          leftIcon={<Trash2 size={16} aria-hidden />}
          onClick={() => {
            onDelete(contest);
          }}
        >
          Usuń
        </Button>
      )}
      {contest.status !== "Finished" && canUpdate && (
        <Button
          variant="secondary"
          leftIcon={<Pencil size={16} aria-hidden />}
          onClick={() => {
            onEdit(contest);
          }}
        >
          Edytuj
        </Button>
      )}
      {contest.status === "Draft" && canUpdate && (
        <Button
          leftIcon={<Play size={16} aria-hidden />}
          isLoading={activate.isPending}
          onClick={() => {
            activate.mutate(contest.id, {
              onSuccess: () => {
                toast.success("Konkurs jest aktywny — agenci go widzą.");
              },
              onError: (error) => {
                toast.error(errorMessage(error, "Nie udało się uruchomić"));
              },
            });
          }}
        >
          Uruchom
        </Button>
      )}
      {contest.status === "Active" && canUpdate && (
        <Button
          leftIcon={<Lock size={16} aria-hidden />}
          onClick={() => {
            onFinish(contest);
          }}
        >
          Zakończ i zamroź wyniki
        </Button>
      )}
    </div>
  );

  return (
    <Drawer
      isOpen={contestId !== null}
      onClose={onClose}
      title={contest?.name ?? "Konkurs"}
      subtitle={contest ? formatPeriod(contest) : undefined}
      footer={footer || undefined}
    >
      {isLoading || !data || !contest ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-6 px-6 py-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={STATUS_VARIANTS[contest.status]} dot>
              {STATUS_LABELS[contest.status]}
            </Badge>
            {contest.status === "Finished" && contest.finishedAt && (
              <span className="text-xs text-dim">
                Wyniki zamrożone{" "}
                {new Date(contest.finishedAt).toLocaleDateString("pl-PL")}
              </span>
            )}
          </div>

          {contest.description && (
            <p className="text-sm whitespace-pre-line text-sub">
              {contest.description}
            </p>
          )}

          {data.me && <Standing contest={contest} me={data.me} />}

          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-dim">Miara</dt>
            <dd className="text-main">
              {MEASURE_LABELS[contest.measure]}
              <span className="block text-xs text-dim">
                {MEASURE_HINTS[contest.measure]}
              </span>
            </dd>
            <dt className="text-dim">Zakres</dt>
            <dd className="text-main">{scopeSummary(contest)}</dd>
            <dt className="text-dim">Uczestnicy</dt>
            <dd className="text-main">
              {contest.participantAgentIds.length === 0
                ? "Wszyscy agenci"
                : `Wybrani agenci (${contest.participantAgentIds.length})`}
            </dd>
            <dt className="text-dim">Nagradzanie</dt>
            <dd className="text-main">
              {REWARD_TYPE_LABELS[contest.rewardType]} ·{" "}
              {rewardSummary(contest)}
              {contest.minimumThreshold !== null &&
                contest.rewardType !== "AboveThreshold" && (
                  <span className="block text-xs text-dim">
                    Próg minimalny:{" "}
                    {formatValue(contest.measure, contest.minimumThreshold)}
                  </span>
                )}
            </dd>
          </dl>

          {contest.rewardType === "Tiers" && (
            <ul className="space-y-1 text-sm">
              {contest.tiers.map((t) => (
                <li
                  key={t.threshold}
                  className="flex justify-between gap-2 rounded-lg bg-muted px-3 py-2"
                >
                  <span className="font-medium text-main">
                    od {formatValue(contest.measure, t.threshold)}
                  </span>
                  <span className="text-sub">{t.reward}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="rounded-lg border border-edge-soft p-3">
            <p className="mb-1 text-xs font-medium text-dim">Nagrody</p>
            <p className="text-sm whitespace-pre-line text-main">
              {contest.rewardDescription}
            </p>
          </div>

          {data.ranking && (
            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-main">
                {contest.status === "Draft"
                  ? "Podgląd rankingu (szkic — agenci go nie widzą)"
                  : contest.status === "Finished"
                    ? "Wyniki końcowe"
                    : "Ranking na dziś"}
              </h3>
              <RankingTable contest={contest} ranking={data.ranking} />
            </section>
          )}
        </div>
      )}
    </Drawer>
  );
}

function ConfirmModal({
  contest,
  mode,
  onClose,
  onDone,
}: {
  contest: Contest | null;
  mode: "finish" | "delete";
  onClose: () => void;
  /** Called after the action succeeded. */
  onDone?: () => void;
}) {
  const finish = useFinishContest();
  const remove = useDeleteContest();
  const mutation = mode === "finish" ? finish : remove;

  function handleClose() {
    mutation.reset();
    onClose();
  }

  return (
    <Modal
      isOpen={contest !== null}
      onClose={handleClose}
      title={mode === "finish" ? "Zakończ konkurs" : "Usuń szkic"}
    >
      <div className="space-y-4">
        {mutation.isError && (
          <Alert variant="error">
            {errorMessage(mutation.error, "Operacja nie powiodła się")}
          </Alert>
        )}
        <div className="flex gap-3 rounded-lg bg-amber-50 p-3 dark:bg-amber-900/20">
          <AlertTriangle
            size={20}
            className="shrink-0 text-amber-600 dark:text-amber-400"
            aria-hidden="true"
          />
          <p className="text-sm text-amber-800 dark:text-amber-300">
            {mode === "finish" ? (
              <>
                Ranking konkursu{" "}
                <span className="font-semibold">{contest?.name}</span> zostanie
                zamrożony w obecnym stanie — późniejsze importy i korekty go nie
                zmienią. Upewnij się, że rozliczenia za cały okres są już
                zaimportowane.
              </>
            ) : (
              <>
                Szkic <span className="font-semibold">{contest?.name}</span>{" "}
                zostanie usunięty.
              </>
            )}
          </p>
        </div>
        <div className="flex justify-end gap-3 border-t border-edge-soft pt-4">
          <Button variant="ghost" onClick={handleClose}>
            Anuluj
          </Button>
          <Button
            variant={mode === "finish" ? "primary" : "danger"}
            isLoading={mutation.isPending}
            onClick={() => {
              if (!contest) {
                return;
              }
              mutation.mutate(contest.id, {
                onSuccess: () => {
                  toast.success(
                    mode === "finish"
                      ? "Konkurs zakończony, wyniki zamrożone."
                      : "Usunięto szkic konkursu.",
                  );
                  handleClose();
                  onDone?.();
                },
              });
            }}
          >
            {mode === "finish" ? "Zakończ i zamroź" : "Usuń szkic"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function ContestsPage() {
  const { data, isLoading } = useContests();
  const canCreate = useHasPermission(PERMISSIONS.CONTESTS.CREATE);
  const [openId, setOpenId] = useState<string | null>(null);
  const [editing, setEditing] = useState<ContestTarget | null>(null);
  const [finishing, setFinishing] = useState<Contest | null>(null);
  const [deleting, setDeleting] = useState<Contest | null>(null);

  const items = data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Konkursy"
        description={
          canCreate
            ? "Konkursy sprzedażowe dla agentów. Ranking liczy się sam z importów i rozliczeń."
            : "Aktywne konkursy, Twoje miejsce i postęp do nagrody."
        }
        actions={
          canCreate && (
            <Button
              leftIcon={<Plus size={16} aria-hidden />}
              onClick={() => {
                setEditing({ contest: null });
              }}
            >
              Nowy konkurs
            </Button>
          )
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Trophy size={24} aria-hidden />}
            title="Brak konkursów"
            description={
              canCreate
                ? "Utwórz pierwszy konkurs — zapisze się jako szkic, agenci zobaczą go po uruchomieniu."
                : "Gdy wspólnicy ogłoszą konkurs, zobaczysz tu swoje miejsce i postęp."
            }
          />
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {items.map((item) => (
            <ContestCard
              key={item.contest.id}
              item={item}
              onOpen={() => {
                setOpenId(item.contest.id);
              }}
            />
          ))}
        </div>
      )}

      <ContestDrawer
        contestId={openId}
        onClose={() => {
          setOpenId(null);
        }}
        onEdit={(contest) => {
          setEditing({ contest });
        }}
        onFinish={setFinishing}
        onDelete={setDeleting}
      />
      <ContestModal
        target={editing}
        onClose={() => {
          setEditing(null);
        }}
      />
      <ConfirmModal
        contest={finishing}
        mode="finish"
        onClose={() => {
          setFinishing(null);
        }}
      />
      <ConfirmModal
        contest={deleting}
        mode="delete"
        onClose={() => {
          setDeleting(null);
        }}
        onDone={() => {
          setOpenId(null);
        }}
      />
    </div>
  );
}
